import { motion } from 'framer-motion';
import { SphereState } from './shaders/sphereShader';

// Progress data interface (matching 3D version)
interface ProgressData {
  stage: string;
  percent: number;  // 0-100
}

interface AudioSphere2DProps {
  audioLevel: number;
  state: SphereState;
  className?: string;
  reducedMotion?: boolean;
  progress?: ProgressData | null;
}

// Color mapping for states (matching 3D version)
const stateColorMap: Record<SphereState, { primary: string; secondary: string; glow: string }> = {
  idle: {
    primary: '#3b82f6',
    secondary: '#2563eb',
    glow: 'rgba(59, 130, 246, 0.4)',
  },
  recording: {
    primary: '#22c55e',
    secondary: '#16a34a',
    glow: 'rgba(34, 197, 94, 0.4)',
  },
  paused: {
    primary: '#f59e0b',
    secondary: '#d97706',
    glow: 'rgba(245, 158, 11, 0.4)',
  },
  encrypting: {
    primary: '#6366f1',
    secondary: '#4f46e5',
    glow: 'rgba(99, 102, 241, 0.4)',
  },
  uploading: {
    primary: '#6366f1',
    secondary: '#4f46e5',
    glow: 'rgba(99, 102, 241, 0.4)',
  },
  complete: {
    primary: '#22c55e',
    secondary: '#16a34a',
    glow: 'rgba(34, 197, 94, 0.4)',
  },
};

// Color mapping for progress stages (matching 3D version)
const progressColorMap: Record<string, { primary: string; secondary: string; glow: string }> = {
  securing: {
    primary: '#6366f1',
    secondary: '#4f46e5',
    glow: 'rgba(99, 102, 241, 0.4)',
  },
  decrypting: {
    primary: '#8b5cf6',
    secondary: '#7c3aed',
    glow: 'rgba(139, 92, 246, 0.4)',
  },
  converting: {
    primary: '#a855f7',
    secondary: '#9333ea',
    glow: 'rgba(168, 85, 247, 0.4)',
  },
  transcribing: {
    primary: '#06b6d4',
    secondary: '#0891b2',
    glow: 'rgba(6, 182, 212, 0.4)',
  },
  enhancing: {
    primary: '#14b8a6',
    secondary: '#0d9488',
    glow: 'rgba(20, 184, 166, 0.4)',
  },
  finalizing: {
    primary: '#22c55e',
    secondary: '#16a34a',
    glow: 'rgba(34, 197, 94, 0.4)',
  },
  complete: {
    primary: '#22c55e',
    secondary: '#16a34a',
    glow: 'rgba(34, 197, 94, 0.4)',
  },
};

export default function AudioSphere2D({
  audioLevel,
  state,
  className = '',
  reducedMotion = false,
  progress
}: AudioSphere2DProps) {
  // Use progress colors if progress is active, otherwise use state colors
  const colors = (progress?.stage && progressColorMap[progress.stage])
    ? progressColorMap[progress.stage]
    : (stateColorMap[state] || stateColorMap.idle);
  const normalizedLevel = audioLevel / 100;

  // Number of concentric rings
  const ringCount = 5;

  // Progress ring calculation
  const progressPercent = progress?.percent || 0;
  const progressRadius = 68;
  const progressCircumference = 2 * Math.PI * progressRadius;
  const progressOffset = progressCircumference * (1 - progressPercent / 100);

  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      {/* Glow background */}
      <motion.div
        className="absolute rounded-full blur-3xl"
        style={{
          width: '80%',
          height: '80%',
          background: colors.glow,
        }}
        animate={reducedMotion ? {} : {
          scale: [1, 1.1 + normalizedLevel * 0.2, 1],
          opacity: [0.3, 0.5 + normalizedLevel * 0.2, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Concentric rings */}
      <svg
        viewBox="0 0 200 200"
        className="w-full h-full"
        style={{ maxWidth: '300px', maxHeight: '300px' }}
      >
        <defs>
          <radialGradient id={`sphereGradient-${state}-${progress?.stage || 'none'}`} cx="30%" cy="30%">
            <stop offset="0%" stopColor={colors.primary} stopOpacity="0.9" />
            <stop offset="70%" stopColor={colors.secondary} stopOpacity="0.8" />
            <stop offset="100%" stopColor={colors.secondary} stopOpacity="0.6" />
          </radialGradient>

          <filter id="sphereGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer rings that pulse with audio */}
        {Array.from({ length: ringCount }).map((_, i) => {
          const baseRadius = 70 + i * 12;
          const delay = i * 0.15;

          return (
            <motion.circle
              key={i}
              cx="100"
              cy="100"
              r={baseRadius}
              fill="none"
              stroke={colors.primary}
              strokeWidth={1.5 - i * 0.2}
              strokeOpacity={0.3 - i * 0.05}
              initial={{ r: baseRadius, opacity: 0.3 - i * 0.05 }}
              animate={reducedMotion ? {} : {
                r: [baseRadius, baseRadius + normalizedLevel * 8, baseRadius],
                opacity: [
                  0.3 - i * 0.05,
                  0.5 - i * 0.05 + normalizedLevel * 0.2,
                  0.3 - i * 0.05,
                ],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay,
              }}
            />
          );
        })}

        {/* Progress ring (when progress is active) */}
        {progressPercent > 0 && (
          <>
            {/* Background track */}
            <circle
              cx="100"
              cy="100"
              r={progressRadius}
              fill="none"
              stroke={colors.secondary}
              strokeWidth="4"
              strokeOpacity="0.2"
            />
            {/* Progress arc */}
            <motion.circle
              cx="100"
              cy="100"
              r={progressRadius}
              fill="none"
              stroke={colors.primary}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={progressCircumference}
              strokeDashoffset={progressOffset}
              initial={{ strokeDashoffset: progressCircumference }}
              animate={{ strokeDashoffset: progressOffset }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: '100px 100px',
              }}
            />
          </>
        )}

        {/* Main sphere circle */}
        <motion.circle
          cx="100"
          cy="100"
          r="60"
          fill={`url(#sphereGradient-${state}-${progress?.stage || 'none'})`}
          filter="url(#sphereGlow)"
          initial={{ scale: 1 }}
          animate={reducedMotion ? { opacity: [0.8, 1, 0.8] } : {
            scale: [1, 1 + normalizedLevel * 0.15, 1],
          }}
          transition={{
            duration: reducedMotion ? 3 : 0.5,
            repeat: Infinity,
            ease: 'easeOut',
          }}
          style={{ transformOrigin: '100px 100px' }}
        />

        {/* Inner highlight for 3D effect */}
        <circle
          cx="85"
          cy="85"
          r="25"
          fill="white"
          fillOpacity="0.15"
        />

        {/* Smaller inner highlight */}
        <circle
          cx="80"
          cy="80"
          r="10"
          fill="white"
          fillOpacity="0.2"
        />
      </svg>

      {/* Reflection */}
      <div
        className="absolute bottom-2 w-1/2 h-4 rounded-full blur-lg opacity-20"
        style={{
          background: `linear-gradient(to top, ${colors.primary}, transparent)`,
        }}
      />
    </div>
  );
}
